import InfiniteScroll from 'react-infinite-scroll-component';
import { format } from 'date-fns';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { MoreButtonsNav } from '@/modules/issuer-dashboard/components/modals/IssuerNavInfoModal.tsx';
import { useIssuerNavListQuery } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { Badge, ChartEmptyState, Spinner } from 'ui';
import { goodFormatUSDCurrency } from 'ui/utils';
import { NAVHistoryItemResponse } from 'api';

interface NAVHistoryListProps {
  register_class_id?: number;
  limit?: number;
  createdDates?: [Dayjs | null, Dayjs | null] | null;
  activeDates?: [Dayjs | null, Dayjs | null] | null;
  blockIdInfinite?: string;
}

export const NAVHistoryList = ({
  register_class_id,
  blockIdInfinite,
  limit,
  createdDates,
  activeDates,
}: NAVHistoryListProps) => {
  const {
    hasNextPage,
    fetchNextPage,
    data: navList,
    isLoading,
    isError,
  } = useIssuerNavListQuery(
    {
      page: 1,
      page_size: limit,
      register_class_id: register_class_id ?? 0,
      active_from: activeDates?.[0] ? dayjs(activeDates[0]).format('YYYY-MM-DD') : undefined,
      active_to: activeDates?.[1] ? dayjs(activeDates[1]).format('YYYY-MM-DD') : undefined,
      created_at_from: createdDates?.[0] ? dayjs(createdDates[0]).format('YYYY-MM-DD') : undefined,
      created_at_to: createdDates?.[1] ? dayjs(createdDates[1]).format('YYYY-MM-DD') : undefined,
    },
    !register_class_id,
  );

  const navs = useMemo(() => (navList?.pages || []).flatMap(({ data }) => data), [navList]);
  const activeNavDate = useMemo(() => {
    const activeNav = navs.find(item => item.is_active === true);
    return new Date(activeNav?.changes_date ?? new Date());
  }, [navs]);

  return (
    <InfiniteScroll
      next={fetchNextPage}
      dataLength={navList?.pages.length ?? 0}
      hasMore={hasNextPage || false}
      style={{ overflow: 'unset' }}
      loader={
        <div className="flex justify-center items-center h-16 w-full">
          <Spinner size="md" variant="primary" />
        </div>
      }
      scrollableTarget={blockIdInfinite}
    >
      <div className="w-full flex flex-col">
        {!navs?.length && (
          <div className="py-4 flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isError={isError} isLoading={isLoading} />
          </div>
        )}
        {navs?.map((item, i, all) => (
          <NAVHistoryItem
            key={'Item_' + item.id}
            data={item}
            i={i}
            all={all}
            activeNavDate={activeNavDate}
          />
        ))}
      </div>
    </InfiniteScroll>
  );
};

interface NAVHistoryItemProps {
  all: NAVHistoryItemResponse[];
  i: number;
  data: NAVHistoryItemResponse;
  activeNavDate: Date;
}

const NAVHistoryItem = ({ activeNavDate, i, all, data }: NAVHistoryItemProps) => {
  return (
    <div className="flex gap-4 justify-between items-center border-t border-t-gray-200 py-2">
      <div className="flex flex-col gap-1">
        <div className="text-sm leading-[22px] font-[500] text-gray-900 flex items-center gap-6">
          <span>{goodFormatUSDCurrency(data.price_per_share)}</span>
          {data.is_active && (
            <span>
              <Badge className="bg-[#0369A1] text-white text-xs leading-5 font-[500] py-0">
                Actual
              </Badge>
            </span>
          )}
        </div>
        <div className="text-xs leading-5 text-gray-400">
          Active From: {format(new Date(data.changes_date), 'dd-MM-yyyy')} | Created At:{' '}
          {format(new Date(data.created_at), 'dd-MM-yyyy HH:mm')}
        </div>
      </div>
      <div>
        <MoreButtonsNav i={i} all={all} data={data} activeNavDate={activeNavDate} />
      </div>
    </div>
  );
};
